/**
 * What the wall is showing, in a short line of words, for the owner's Note key (v26.10.027, a
 * temporary test aid). The projector fills in what it has on screen; this only puts it in words,
 * so it can be tested without a browser.
 */

export interface WallView {
  /** False until the wall has the day. */
  loaded: boolean;
  /** A slide before round 1, 1-based. */
  slide: { n: number; of: number; title: string } | null;
  /** The join wall before round 1: who is in the hall. */
  beforeRound1: { here: number; total: number } | null;
  /** The results card that is up. */
  results: number | null;
  /** A round being played, and what is left on its clock. */
  playing: { round: number; leftMs: number } | null;
  /** The board between rounds, after this round (null before any has finished). */
  between: { after: number | null } | null;
  /** The twist the whole hall is under. */
  twist: string | null;
  /** The card over the wall: "Twist" or "Mini game", and what it names. */
  card: { label: string; names: string[] } | null;
  /** The pause screen: a break with a time ("Lunch", "Back at 13:47"), or a plain pause with what was left on the clock. */
  pause: { label: string | null; backAt: string | null; over: boolean; leftOnClockMs: number | null } | null;
  /** A podium: the main board or the fun board. */
  podium: "main" | "fun" | null;
  /** The award on the wall. */
  award: { n: number; of: number; title: string } | null;
  /** The admin's message, while it is up. */
  notice: string | null;
  /** ?demo=x on the wall's address. */
  demo: string | null;
}

function minSec(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** "Round 4 playing, 10:53 left. Wall message: Food is ready", and so on. */
export function describeWall(v: WallView): string {
  if (!v.loaded) return "Connecting";
  const parts: string[] = [];
  if (v.slide) parts.push(`Slide ${v.slide.n} of ${v.slide.of}: ${v.slide.title}`);
  else if (v.beforeRound1) parts.push(`Before round 1, join wall, ${v.beforeRound1.here} of ${v.beforeRound1.total} here`);
  else if (v.results !== null) parts.push(`Results card, round ${v.results}`);
  else if (v.playing) parts.push(`Round ${v.playing.round} playing, ${v.playing.leftMs > 0 ? `${minSec(v.playing.leftMs)} left` : "time up"}`);
  else if (v.between) parts.push(v.between.after !== null ? `Between rounds, after round ${v.between.after}` : "Between rounds");
  if (!v.slide) {
    if (v.twist) parts.push(`Twist on: ${v.twist}`);
    if (v.card && v.card.names.length) parts.push(`${v.card.label} card: ${v.card.names.join(", ")}`);
    if (v.pause) {
      const p = v.pause;
      if (p.label) parts.push(`${p.label}${p.over ? ", time to be back has passed" : p.backAt ? `, ${p.backAt.charAt(0).toLowerCase()}${p.backAt.slice(1)}` : ""}`);
      else parts.push(`Paused${p.leftOnClockMs !== null ? `, ${minSec(p.leftOnClockMs)} left on the clock` : ""}`);
    }
    if (v.podium) parts.push(`Podium: ${v.podium === "main" ? "main board" : "fun board"}`);
    if (v.award) parts.push(`Award ${v.award.n} of ${v.award.of}: ${v.award.title}`);
    if (v.notice) parts.push(`Wall message: ${v.notice}`);
  }
  if (v.demo) parts.push(`?demo=${v.demo}`);
  return parts.join(". ") || "Nothing on the wall";
}
